import { isCarsTrucks } from '@kijiji/category'
import dynamic from 'next/dynamic'
import React, { Fragment, useMemo } from 'react'

import { ClientRender } from '@/components/shared/client-render'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { ListingCard } from '@/components/srp/listing-card'
import { InlineAdSlot } from '@/components-page/srp/advertisement/InlineAdSlot'
import { isICOLaunchedInLocation } from '@/domain/location/isICOLaunchedInLocation'
import { SearchListIntersectionObserver } from '@/domain/srp/SearchListIntersectionObserver'
import { useGetSearchResultsData } from '@/hooks/srp'
import { type Listing } from '@/types/search'

type RenderListingCardProps = {
  /**
   * Index of the listing card in the list
   */
  index: number
  /**
   * Flag indicating if this is a top advertisement
   */
  isTopAd?: boolean
  /**
   * Listing data object
   */
  item: Listing
  /**
   * Total number of listings in the search results
   */
  totalListings?: number
  /**
   * Flag indicating if this card is rendered in map view
   */
  isMapCard?: boolean
}

const ValueEstimate = dynamic(
  () => import('@/components/srp/value-estimate').then((mod) => mod.ValueEstimate),
  { ssr: false }
)

/**
 * RenderListingCard component renders a single listing card with associated features
 * like ads and value estimates. It handles intersection observation for lazy loading
 * and includes error boundaries for resilience.
 *
 * @component
 * @param props
 * @returns Rendered listing card with associated features
 */
export const RenderListingCard = ({
  index,
  isTopAd,
  item,
  totalListings,
  isMapCard,
}: RenderListingCardProps) => {
  const { data } = useGetSearchResultsData()
  const { searchQuery, pagination } = data || {}

  const searchProvinceId =
    searchQuery?.location?.locationPaths && searchQuery?.location?.locationPaths.length > 1
      ? searchQuery.location.locationPaths[1].id
      : undefined

  const { totalCount: listingsSize = 0 } = pagination || {}

  /**
   * It should check the current category based on the listing data not activeCategory cache
   * If the active category is ALL CATEGORIES, none of the listing specific logic would work
   */
  const { categoryId: listingCategoryId } = item
  const isCarsTrucksSearch = isCarsTrucks(listingCategoryId)

  // We are interested in the user's location here, not the location of the listing.
  const isICOActive = useMemo(
    () => isICOLaunchedInLocation(searchQuery?.location?.locationPaths),
    [searchQuery?.location?.locationPaths]
  )

  const key = `listing-${item.id}-${index}`

  return (
    <Fragment key={key}>
      <SearchListIntersectionObserver data-testid={`listing-card-list-item-${index}`} index={index}>
        <ErrorBoundary fingerprintId="RenderListingCard">
          <ListingCard item={item} isTopAd={isTopAd} index={index} isMapCard={isMapCard} />
        </ErrorBoundary>
      </SearchListIntersectionObserver>

      {!isTopAd && (
        <>
          <ClientRender>
            <InlineAdSlot
              index={index}
              totalListings={totalListings ?? listingsSize}
              isGridView={false}
            />
          </ClientRender>
          {isCarsTrucksSearch && isICOActive && (
            <ValueEstimate index={index} searchProvinceId={searchProvinceId} />
          )}
        </>
      )}
    </Fragment>
  )
}
